import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { configureOpenIdAuth } from '@addictivegroup/addictive-auth'
import { AddictivePermissions } from '@addictivegroup/addictive-permissions'
import App from './App.vue'
import router from './router/index.js'
import i18n from './i18n/index.js'
import { setupDictation } from './lib/dictation.js'
import { setupRichText } from './lib/richtext.js'
import { pagesFetcher, permissionsFetcher } from './lib/permissionsCache.js'
import { installReauth } from './lib/reauth.js'
import { offline } from './lib/offline.js'
import { resolveApiUrl } from './lib/apiUrl.js'
import { dropStaleCookies } from './lib/staleCookies.js'
import { installAutogrow } from './lib/autogrow.js'
import { useSession } from './stores/session.js'
import { useUi } from './stores/ui.js'
import { restoreTextZoom } from './lib/textZoom.js'
import { vPrivate } from './lib/privateDirective.js'
import { usePrivacy } from './stores/privacy.js'
// Bundled, so text looks the same offline and on devices without the font installed.
import '@fontsource-variable/inter'
import './index.css'

// Core runs the session (addictive-auth): on the panel's own origin (Vite in development and
// nginx proxy /api, /auth and /broadcasting), or at an address of its own (VITE_API_URL, D-217).
export const API_URL = resolveApiUrl(import.meta.env.VITE_API_URL, window.location.host)
configureOpenIdAuth({ backendUrl: API_URL || window.location.origin })
// Before the first request: the panel's own XSRF token and session left from before D-217 (D-247).
dropStaleCookies(API_URL)
installAutogrow()

const app = createApp(App)
const pinia = createPinia()
app.use(pinia)
app.use(i18n)
setupDictation()
setupRichText()
app.use(AddictivePermissions, {
  panelApp: { fetcher: pagesFetcher },
  permissions: { fetcher: permissionsFetcher },
  // A record without an abilities map denies: no answer is not consent.
  abilities: { whenMissing: 'deny', warn: import.meta.env.DEV },
})
app.use(router)
app.directive('private', vPrivate)

// The theme follows the system unless the person chose one.
function applyTheme() {
  let theme = 'system'
  try {
    theme = localStorage.getItem('addcrm:theme') || 'system'
  } catch {
    // default theme
  }
  const dark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  document.documentElement.classList.toggle('dark', dark)
}
applyTheme()
// The text's size this device was left at (D-233), before the first paint.
restoreTextZoom()
// Privacy mode as this device was left in (D-260), before the first paint.
usePrivacy().apply()
window.matchMedia('(prefers-color-scheme: dark)').addEventListener?.('change', applyTheme)
window.addEventListener('addcrm:theme', applyTheme)

// A dead session while the network works: straight to AddZone's sign-in, back here; queued changes
// wait on the device and go after (D-246). A stale XSRF token alone gets a fresh one instead.
installReauth({ router, offline, session: useSession(), notify: () => useUi().warn(i18n.global.t('auth.sessionLost')) })

useUi().restoreRecent()
app.mount('#app')
